import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { useOfflineSync } from '../hooks/useOfflineSync';

const QuizPage = () => {
  const { movieId, language } = useParams();
  const location = useLocation();
  const isGenZQuiz = location.pathname.includes('/quiz/genz/');
  const isPopularQuiz = location.pathname.includes('/quiz/popular/');
  const navigate = useNavigate();
  const { user } = useAuth();
  const [quiz, setQuiz] = useState(null);
  const [movie, setMovie] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState({});
  const [showResults, setShowResults] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [quizSettings] = useState({
    questionCount: 5,
    difficulty: 'easy',
    questionTypes: ['multiple_choice', 'true_false'],
  });

  const { isOnline, addPendingSubmission } = useOfflineSync();

  // Error boundary fallback
  const [renderError, setRenderError] = useState(null);

  useEffect(() => {
    generateQuiz();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [movieId, language]);

  const generateQuiz = async () => {
    setLoading(true);
    setError(null);

    // Check if offline
    if (!isOnline) {
      setError('You are currently offline. Please check your internet connection and try again.');
      setLoading(false);
      return;
    }

    try {
      let endpoint;
      if (movieId) {
        endpoint = `/api/quiz/generate/${movieId}`;
      } else if (language) {
        if (isGenZQuiz) {
          endpoint = `/api/quiz/generate/genz/${language}`;
        } else if (isPopularQuiz) {
          endpoint = `/api/quiz/generate/popular/${language}`;
        } else {
          endpoint = `/api/quiz/generate/random/${language}`;
        }
      } else {
        throw new Error('No movie or language specified');
      }

      const response = await axios.post(endpoint, quizSettings);

      if (response.data.success) {
        // Handle different response formats
        const quizData =
          response.data.data.quiz || response.data.data.questions || response.data.data;
        setQuiz(quizData);
        setMovie(response.data.data.movie || response.data.data.metadata);
        setCurrentQuestion(0);
        setSelectedAnswers({});
        setShowResults(false);
        console.log('Quiz data loaded:', quizData);
      } else {
        setError(response.data.error || 'Failed to generate quiz');
      }
    } catch (error) {
      console.error('Quiz generation error:', error);
      if (error.response?.status === 429) {
        setError('API rate limit reached. Please try again in a few minutes.');
      } else if (!isOnline) {
        setError('Connection lost. Please check your internet connection.');
      } else {
        setError(error.response?.data?.error || 'Failed to generate quiz. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAnswerSelect = (questionId, answer) => {
    setSelectedAnswers(prev => ({
      ...prev,
      [questionId]: answer,
    }));
  };

  const handleNext = () => {
    const questions = Array.isArray(quiz) ? quiz : quiz && quiz.questions ? quiz.questions : [];
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(prev => prev + 1);
    } else {
      setShowResults(true);
      // Submit quiz results when quiz is completed
      submitQuizResults();
    }
  };

  const submitQuizResults = async () => {
    const score = calculateScore();
    const results = {
      movieId: movie?.id,
      movieName: movie?.name || movie?.movieTitle,
      score,
      quizType: isGenZQuiz ? 'genz' : movieId ? 'movie' : 'random',
      language: movie?.language || language || 'english',
      completedAt: new Date().toISOString(),
    };

    console.log('🎯 Submitting quiz results:', results);
    console.log('🔐 Auth token exists:', !!localStorage.getItem('authToken'));
    console.log('👤 User logged in:', !!user);

    if (isOnline) {
      try {
        const response = await axios.post('/api/quiz/submit', results);
        console.log('✅ Quiz submission successful:', response.data);
      } catch (error) {
        console.error('❌ Quiz submission failed:', error);
        console.log('Failed to submit results online, storing for later sync');
        addPendingSubmission(results);
      }
    } else {
      // Store for offline sync
      addPendingSubmission(results);
    }
  };

  const handlePrevious = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(prev => prev - 1);
    }
  };

  const calculateScore = () => {
    const questions = Array.isArray(quiz) ? quiz : quiz && quiz.questions ? quiz.questions : [];
    const totalQuestions = questions.length;
    const correctAnswers = questions.filter((q, index) => {
      return selectedAnswers[q.id] === q.correctAnswer;
    }).length;
    return { correct: correctAnswers, total: totalQuestions };
  };

  const resetQuiz = () => {
    setCurrentQuestion(0);
    setSelectedAnswers({});
    setShowResults(false);
  };

  if (loading) {
    return (
      <div
        style={{
          minHeight: '100vh',
          padding: '2rem 0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <div className='text-center'>
          <div className='loading-spinner' style={{ margin: '0 auto 1rem' }}></div>
          <p style={{ color: 'white', fontSize: '1.2rem' }}>Generating your quiz...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ minHeight: '100vh', padding: '2rem 0' }}>
        <div className='container'>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '2rem' }}>
            <button
              onClick={() => navigate('/')}
              className='btn btn-secondary'
              style={{ marginRight: '1rem' }}
            >
              ← Back
            </button>
            <h1 style={{ fontSize: '2rem', color: 'white', margin: 0 }}>Quiz Generation Failed</h1>
          </div>

          <div className='card text-center'>
            <span style={{ fontSize: '4rem', display: 'block', marginBottom: '1rem' }}>❌</span>
            <h2 style={{ fontSize: '1.5rem', color: 'white', marginBottom: '1rem' }}>
              Oops! Something went wrong
            </h2>
            <p style={{ color: 'rgba(255,255,255,0.8)', marginBottom: '2rem' }}>{error}</p>
            <div className='grid grid-2'>
              <button onClick={() => navigate('/')} className='btn btn-secondary'>
                Go Home
              </button>
              <button onClick={generateQuiz} className='btn btn-primary'>
                Try Again
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!quiz) return null;

  // Handle different quiz data formats
  const questions = Array.isArray(quiz) ? quiz : quiz && quiz.questions ? quiz.questions : [];
  if (questions.length === 0) return null;

  const currentQ = questions[currentQuestion];
  if (!currentQ) return null;

  // Ensure movie object exists with fallback
  const movieData = movie || { name: 'Movie Quiz', language: 'Unknown' };
  const score = showResults ? calculateScore() : null;

  // Render error fallback
  if (renderError) {
    return (
      <div style={{ minHeight: '100vh', padding: '2rem 0' }}>
        <div className='container'>
          <div className='card text-center'>
            <span style={{ fontSize: '4rem', display: 'block', marginBottom: '1rem' }}>⚠️</span>
            <h2 style={{ fontSize: '1.5rem', color: 'white', marginBottom: '1rem' }}>
              Something went wrong
            </h2>
            <p style={{ color: 'rgba(255,255,255,0.8)', marginBottom: '2rem' }}>
              There was an error displaying the quiz. Please try refreshing the page.
            </p>
            <div className='grid grid-2'>
              <button onClick={() => navigate('/')} className='btn btn-secondary'>
                Go Home
              </button>
              <button onClick={() => window.location.reload()} className='btn btn-primary'>
                Refresh Page
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  try {
    return (
      <div style={{ minHeight: '100vh', padding: '2rem 0' }}>
        <div className='container'>
          {/* Offline indicator */}
          {!isOnline && (
            <div
              style={{
                backgroundColor: '#ff8c00',
                color: 'white',
                padding: '0.75rem 1rem',
                borderRadius: '0.5rem',
                marginBottom: '1rem',
                textAlign: 'center',
                boxShadow: '0 4px 12px rgba(255, 140, 0, 0.3)',
              }}
            >
              📱 You're offline - Quiz results will sync when you're back online
            </div>
          )}

          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '2rem' }}>
            <button
              onClick={() => navigate('/')}
              className='btn btn-secondary'
              style={{ marginRight: '1rem' }}
            >
              ← Back
            </button>
            <div>
              <h1
                style={{
                  fontSize: '2rem',
                  color: 'white',
                  margin: 0,
                  textShadow: '2px 2px 4px rgba(0,0,0,0.5)',
                }}
              >
                {movieData?.name || movieData?.movieTitle || 'Movie Quiz'}
              </h1>
              <p style={{ color: '#ff8c00', margin: 0, fontWeight: '500' }}>
                {movieData?.year && `${movieData.year} • `}
                {movieData?.language || 'Unknown'}
              </p>
            </div>
          </div>

          {showResults ? (
            <div>
              <div className='card text-center mb-8'>
                <span style={{ fontSize: '4rem', display: 'block', marginBottom: '1rem' }}>
                  {score.correct === score.total
                    ? '🎉'
                    : score.correct >= score.total * 0.7
                      ? '👏'
                      : '📚'}
                </span>
                <h2 style={{ fontSize: '2rem', color: 'white', marginBottom: '1rem' }}>
                  Quiz Complete!
                </h2>
                <p
                  style={{
                    fontSize: '1.5rem',
                    color: 'rgba(255,255,255,0.9)',
                    marginBottom: '0.5rem',
                  }}
                >
                  You scored {score.correct} out of {score.total}
                </p>
                <p style={{ color: 'rgba(255,255,255,0.7)', marginBottom: '2rem' }}>
                  {Math.round((score.correct / score.total) * 100)}% correct
                </p>

                <div className='grid grid-3'>
                  <button onClick={resetQuiz} className='btn btn-secondary'>
                    Retake Quiz
                  </button>
                  <button onClick={generateQuiz} className='btn btn-primary'>
                    New Quiz
                  </button>
                  <button onClick={() => navigate('/')} className='btn btn-secondary'>
                    Home
                  </button>
                </div>
              </div>

              <div style={{ marginBottom: '2rem' }}>
                {questions.map((question, index) => {
                  const userAnswer = selectedAnswers[question.id];
                  const isCorrect = userAnswer === question.correctAnswer;

                  return (
                    <div key={question.id} className='card mb-4'>
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          justifyContent: 'space-between',
                          marginBottom: '1rem',
                        }}
                      >
                        <h3
                          style={{
                            fontSize: '1.1rem',
                            color: 'white',
                            margin: 0,
                            flex: 1,
                            marginRight: '1rem',
                          }}
                        >
                          {index + 1}. {question.question}
                        </h3>
                        <span style={{ fontSize: '1.5rem' }}>{isCorrect ? '✅' : '❌'}</span>
                      </div>

                      <div style={{ marginBottom: '1rem' }}>
                        <p style={{ color: 'rgba(255,255,255,0.8)', marginBottom: '0.5rem' }}>
                          <strong>Your answer:</strong> {userAnswer || 'Not answered'}
                        </p>
                        <p style={{ color: '#ff8c00', marginBottom: '0.5rem' }}>
                          <strong>Correct answer:</strong> {question.correctAnswer}
                        </p>
                      </div>

                      <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.9rem' }}>
                        {question.explanation}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <div>
              <div style={{ marginBottom: '2rem' }}>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    color: 'white',
                    marginBottom: '0.5rem',
                  }}
                >
                  <span>
                    Question {currentQuestion + 1} of {questions.length}
                  </span>
                  <span>{Math.round(((currentQuestion + 1) / questions.length) * 100)}%</span>
                </div>
                <div
                  style={{
                    width: '100%',
                    backgroundColor: 'rgba(255,255,255,0.2)',
                    borderRadius: '0.5rem',
                    height: '0.5rem',
                  }}
                >
                  <div
                    style={{
                      backgroundColor: '#ff8c00',
                      height: '0.5rem',
                      borderRadius: '0.5rem',
                      transition: 'width 0.3s ease',
                      width: `${((currentQuestion + 1) / questions.length) * 100}%`,
                      boxShadow: '0 2px 8px rgba(255, 140, 0, 0.4)',
                    }}
                  ></div>
                </div>
              </div>

              <div className='card mb-8'>
                <div style={{ marginBottom: '1.5rem' }}>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      marginBottom: '1rem',
                    }}
                  >
                    <span
                      style={{
                        color: '#ff8c00',
                        fontSize: '0.875rem',
                        fontWeight: '600',
                        textTransform: 'uppercase',
                      }}
                    >
                      {currentQ?.type?.replace('_', ' ') || 'Question'} •{' '}
                      {currentQ?.difficulty || 'Easy'}
                    </span>
                    <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.875rem' }}>
                      {currentQ?.category || 'General'}
                    </span>
                  </div>
                  <h2 style={{ fontSize: '1.5rem', color: 'white', marginBottom: '1.5rem' }}>
                    {currentQ?.question || 'Loading question...'}
                  </h2>
                </div>

                <div style={{ marginBottom: '2rem' }}>
                  {currentQ?.type === 'single_answer' ? (
                    <input
                      type='text'
                      value={selectedAnswers[currentQ?.id] || ''}
                      onChange={e => handleAnswerSelect(currentQ?.id, e.target.value)}
                      placeholder='Type your answer...'
                      className='form-input'
                      style={{ fontSize: '1rem' }}
                    />
                  ) : (
                    <div style={{ display: 'grid', gap: '0.75rem' }}>
                      {(currentQ?.options || []).map((option, index) => {
                        const isSelected = selectedAnswers[currentQ?.id] === option;

                        return (
                          <button
                            key={index}
                            onClick={() => handleAnswerSelect(currentQ?.id, option)}
                            style={{
                              width: '100%',
                              padding: '1rem',
                              textAlign: 'left',
                              borderRadius: '0.5rem',
                              border: `1px solid ${isSelected ? '#ff8c00' : 'rgba(255,255,255,0.3)'}`,
                              backgroundColor: isSelected
                                ? 'rgba(255, 140, 0, 0.3)'
                                : 'rgba(255,255,255,0.1)',
                              color: 'white',
                              cursor: 'pointer',
                              transition: 'all 0.2s ease',
                            }}
                            onMouseEnter={e => {
                              if (!isSelected) {
                                e.target.style.backgroundColor = 'rgba(255,255,255,0.15)';
                                e.target.style.borderColor = 'rgba(255,255,255,0.5)';
                              }
                            }}
                            onMouseLeave={e => {
                              if (!isSelected) {
                                e.target.style.backgroundColor = 'rgba(255,255,255,0.1)';
                                e.target.style.borderColor = 'rgba(255,255,255,0.3)';
                              }
                            }}
                          >
                            <span style={{ fontWeight: '600', marginRight: '0.75rem' }}>
                              {String.fromCharCode(65 + index)}.
                            </span>
                            {option}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <button
                  onClick={handlePrevious}
                  disabled={currentQuestion === 0}
                  className='btn btn-secondary'
                  style={{ opacity: currentQuestion === 0 ? 0.5 : 1 }}
                >
                  Previous
                </button>

                <button
                  onClick={handleNext}
                  disabled={!selectedAnswers[currentQ?.id]}
                  className='btn btn-primary'
                  style={{ opacity: !selectedAnswers[currentQ?.id] ? 0.5 : 1 }}
                >
                  {currentQuestion === (questions?.length || 1) - 1 ? 'Finish Quiz' : 'Next'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  } catch (err) {
    console.error('QuizPage render error:', err);
    setRenderError(err);
    return null;
  }
};

export default QuizPage;
